import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CCardFooter, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CFormFeedback, CSpinner
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DateInput from 'components/forms/DateInput';
import { jwtDecode } from 'jwt-decode';
import TextEditor from "components/forms/TextEditor";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from 'components/globals/AlertNotification';

const Internship = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const location = useLocation();
    const [toast, setToast] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_manager = decodedToken.is_manager;

    const fetchInternshipDetails = async () => {
        let data = null; // Declare a variable to store API response
        try {
            const response = await apiClient.get(`/internships/${id}/`);
            data = response.data; // Store response data
            setFormData(data); // Update state
        } catch (error) {
            triggerAlert('Estágio não encontrada', 'error');
            navigate("/estagios");
        }
    };

    useEffect(() => {
        if (!id) {
            setFormData({});
        } else {
            fetchInternshipDetails();
        }
    }, [id, location.pathname]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
    
        let updatedValue;
        if (type === "checkbox") {
            updatedValue = checked;
        } else if (value === "true") {
            updatedValue = true;
        } else if (value === "false") {
            updatedValue = false;
        } else if (value === "") {
            updatedValue = null;
        } else {
            updatedValue = value;
        }
    
        setFormData((prevFormData) => {
            const updatedFormData = {
                ...prevFormData,
                [name]: updatedValue,
            };
    
            return updatedFormData;
        });
    
        setFieldErrors((prevErrors) => {
            const updated = { ...prevErrors };
            delete updated[name];
            return updated;
        });    
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (id) {
                await apiClient.put(`/internships/${id}/`, formData);
                triggerAlert('Estágio atualizada com sucesso!', 'success');
                navigate(`/estagios`);
            } else {
                await apiClient.post('/internships/', formData);
                triggerAlert('Estágio criada com sucesso!', 'success');
                navigate(`/estagios`);
            }
        } catch (error) {
            const errors = error.response?.data;
            let errorMessage = 'Erro ao gravar';

            if (errors && typeof errors === 'object') {
                setFieldErrors(errors); // <-- Save the full error object
            } else {
                setFieldErrors({});
            }

            setTimeout(() => {
                const firstInvalidInput = document.querySelector('input.is-invalid, textarea.is-invalid, select.is-invalid');
                if (firstInvalidInput) {
                    firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    firstInvalidInput.focus();
                }
            }, 50);


            setToast({ title: 'Estágio', message: errorMessage, type: 'error' });
            console.log('Error submitting form:', error);
            setLoading(false);
        }
    };

    if (formData !== null) {
        return (
            <div className="container mt-4">
                {/* Error Toast - Appears on submit failure */}
                <ToastNotification toast={toast} setToast={setToast} />

                <CCard>
                    <CCardHeader>
                        <h4>Fornecedor</h4>
                    </CCardHeader>
                    <CCardBody>
                        <CForm onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Nome</strong>}
                                    name="name"
                                    value={formData.name ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.name}
                                />
                                {fieldErrors.name && (
                                    <CFormFeedback invalid>{fieldErrors.name[0]}</CFormFeedback>
                                )}
                            </div>

                            <div className="mb-3">
                                <CFormInput
                                    type="email"
                                    label={<strong>Email</strong>}
                                    name="email"
                                    value={formData.email ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.email}
                                />
                                {fieldErrors.email && (
                                    <CFormFeedback invalid>{fieldErrors.email[0]}</CFormFeedback>
                                )}
                            </div>

                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Telefone</strong>}
                                    name="phone_number"
                                    value={formData.phone_number ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.phone_number}
                                />
                                {fieldErrors.phone && (
                                    <CFormFeedback invalid>{fieldErrors.phone_number[0]}</CFormFeedback>
                                )}
                            </div>

                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Instituição</strong>}
                                    name="institution"
                                    value={formData.institution ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.institution}
                                />
                                {fieldErrors.institution && (
                                    <CFormFeedback invalid>{fieldErrors.institution[0]}</CFormFeedback>
                                )}
                            </div>

                            <DateInput
                                label={<strong>Data de Início</strong>}
                                name="start_date"
                                value={formData.start_date ?? ''}
                                onChange={handleInputChange}
                            />

                            <DateInput
                                label={<strong>Data de Fim</strong>}
                                name="end_date"
                                value={formData.end_date ?? ''}
                                onChange={handleInputChange}
                            />
                        </CForm>
                    </CCardBody>
                    <CCardFooter className="d-flex justify-content-end gap-2">
                        <CButton color="danger" className="text-white" onClick={() => navigate('/estagios')}>
                            Cancelar
                        </CButton>
                        <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={handleSubmit}>
                            {loading ? <CSpinner size="sm" /> : 'Confirmar'}
                        </CButton>
                    </CCardFooter>
                </CCard>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <h2>Loading...</h2>
        </div>
    );
};

export default Internship;
